import React from 'react';
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonBackButton,
  IonContent,
  IonCard,
  IonCardHeader,
  IonCardTitle,
  IonCardSubtitle,
  IonCardContent,
  IonButton,
  IonSpinner,
  IonText,
  IonToast,
  IonModal,
  IonList,
  IonItem,
  IonLabel,
  IonCheckbox,
  IonBadge,
  IonIcon,
} from '@ionic/react';
import { closeOutline, cloudDownloadOutline } from 'ionicons/icons';
import { useAdminFirmware } from '../../../application/use-cases/admin/useAdminFirmware';
import { UPDATE_STATUS_LABEL } from '../../../domain/Hub';
import { formatRelative } from '../../../domain/WeatherStation';
import './Admin.css';

const AdminFirmware: React.FC = () => {
  const { releases, hubs, loading, error, toast, setToast, deploying, setDeploying, selected, toggle, openDeploy, deploy, busy } =
    useAdminFirmware();

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/app/admin" />
          </IonButtons>
          <IonTitle>Firmware de los hubs</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent fullscreen className="ion-padding">
        <p className="admin-list-row-meta">
          Las versiones se publican desde el taller (firmware/tools/publicar.py), firmadas con la clave de DomIoT. Mandá cada
          una primero a uno o dos hubs; si quedan &quot;Actualizado&quot;, al resto. Un hub que no logra conectarse con la versión nueva
          vuelve solo a la anterior.
        </p>

        {loading && (
          <div className="ion-text-center">
            <IonSpinner color="primary" />
          </div>
        )}
        {error && (
          <IonText color="danger">
            <p>{error}</p>
          </IonText>
        )}
        {!loading && releases.length === 0 && <p className="admin-list-row-meta">Todavía no se publicó ninguna versión.</p>}

        {releases.map((release) => (
          <IonCard key={release.id} className="admin-detail-card">
            <IonCardHeader>
              <IonCardTitle>
                <span className="admin-card-icon tone-blue">
                  <IonIcon icon={cloudDownloadOutline} />
                </span>
                Versión {release.version}
              </IonCardTitle>
              <IonCardSubtitle>
                {formatRelative(release.createdAt)} · {Math.round(release.size / 1024)} KB
                {release.notes && ` · ${release.notes}`}
              </IonCardSubtitle>
            </IonCardHeader>
            <IonCardContent>
              {release.updates.length > 0 && (
                <IonList>
                  {release.updates.map((u) => (
                    <IonItem key={u.hubId} lines="inset">
                      <IonLabel>
                        <h3>{u.hubId}</h3>
                        <p>
                          {formatRelative(u.updatedAt)}
                          {u.detail && ` · ${u.detail}`}
                        </p>
                      </IonLabel>
                      <IonBadge slot="end" color={UPDATE_STATUS_LABEL[u.status].tone}>
                        {UPDATE_STATUS_LABEL[u.status].text}
                      </IonBadge>
                    </IonItem>
                  ))}
                </IonList>
              )}
              <IonButton expand="block" fill="outline" onClick={() => openDeploy(release)} style={{ marginTop: 8 }}>
                Mandar a hubs…
              </IonButton>
            </IonCardContent>
          </IonCard>
        ))}

        <IonModal isOpen={!!deploying} onDidDismiss={() => setDeploying(null)}>
          <IonHeader>
            <IonToolbar>
              <IonTitle>Mandar la {deploying?.version}</IonTitle>
              <IonButtons slot="end">
                <IonButton onClick={() => setDeploying(null)} aria-label="Cerrar">
                  <IonIcon icon={closeOutline} />
                </IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent className="ion-padding">
            <p className="admin-list-row-meta">Solo les llega a los hubs en línea.</p>
            <IonList>
              {hubs.map((hub) => {
                const current = hub.lastInfo?.fw;
                const upToDate = current === deploying?.version;
                return (
                  <IonItem key={hub.id}>
                    <IonCheckbox
                      slot="start"
                      checked={selected.includes(hub.id)}
                      disabled={upToDate}
                      onIonChange={() => toggle(hub.id)}
                    />
                    <IonLabel>
                      <h3>
                        {hub.id} · {hub.status === 'online' ? 'en línea' : 'sin conexión'}
                      </h3>
                      <p>
                        {hub.ownerEmail ?? 'stock'} · tiene la {current ?? '¿?'}
                        {upToDate && ' (ya está al día)'}
                      </p>
                    </IonLabel>
                  </IonItem>
                );
              })}
            </IonList>
            <IonButton expand="block" onClick={deploy} disabled={busy || selected.length === 0} style={{ marginTop: 16 }}>
              {busy ? 'Mandando…' : `Actualizar ${selected.length} hub${selected.length === 1 ? '' : 's'}`}
            </IonButton>
          </IonContent>
        </IonModal>

        <IonToast isOpen={!!toast} message={toast ?? ''} duration={4000} onDidDismiss={() => setToast(null)} />
      </IonContent>
    </IonPage>
  );
};

export default AdminFirmware;
